import type { ColDef, GetRowIdParams, SortChangedEvent, SortDirection } from 'ag-grid-community';
import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { useStatisticFilters } from '../../hooks/filters';
import { useStatisticSorting } from '../../hooks/sorting';
import { useStatistics } from '../../hooks/stats';
import { useStatisticTypes } from '../../hooks/statTypes';
import { useStatisticOptions } from '../../store';
import Loader from '../Loader';

import { EmptyMessage } from './EmptyMessage';
import Identifier from './Identifier';

import { useWindowSize } from '@hooks/useWindowSize';
import DataTableGrid from '@shared/DataTableGrid';
import type { Order } from '@shared-types/common.types';
import type { Player, Team } from '@shared-types/match.types';
import type { StatisticDefinition } from '@shared-types/stats.types';
import { getStatValue } from '@utilities/stats';

import * as styles from './styles.module.scss';

const columnTypes: Record<string, ColDef> = {
  identifier: {
    cellClass: 'ag-cell--uefa_identifier pk-justify-content--start',
    cellDataType: 'identifier',
    cellRenderer: Identifier,
    resizable: false,
    sortable: false,
    suppressMovable: true,
  },
} as const;

const defaultColDef: ColDef = {
  autoHeaderHeight: true,
  sortingOrder: ['desc', 'asc'],
  context: { useShortLabel: false },
} as const;

const getRowId = (row: GetRowIdParams<{ identifier: Team | Player }>) => row.data?.identifier?.id;

export const Table = () => {
  const scrollSpyRef = useRef<HTMLDivElement>(null);
  const scrolled = useViewportSpy(scrollSpyRef);
  const {
    options: { statType },
  } = useStatisticOptions();

  const { stats, loadMoreStats, isFetched, isFetchingNextPage } = useStatistics();
  const { sorting, order, setSorting } = useStatisticSorting();
  const { types } = useStatisticTypes();
  const { filters } = useStatisticFilters();
  const { isMobile, isDesktop } = useWindowSize();

  useEffect(() => {
    if (!scrolled) return;

    loadMoreStats();
  }, [loadMoreStats, scrolled]);

  const handleSortingChange = (event: SortChangedEvent) => {
    const column = event.columns.find((c) => c.getSort());
    if (!column) return;

    const statDef = types.find((sd) => sd.name === column.getId());
    const sort = column.getSort()?.toUpperCase() as Order;
    setSorting(statDef, sort);
  };

  const statIndex = useRef<number>(1);

  const getRowIndex = useCallback(
    (index: number) => {
      if (index === 0) {
        statIndex.current = 0;
      }

      if (index > 0 && stats?.[index] && stats?.[index - 1]) {
        const prevValue = getStatValue(stats[index - 1], sorting);
        const currentValue = getStatValue(stats[index], sorting);

        if (prevValue && prevValue !== currentValue) {
          statIndex.current = index;
        }
      }

      return `${statIndex.current + 1}`.padStart(2, '0');
    },
    [sorting, stats],
  );

  const hasFiltersApplied = useMemo(() => filters && Object.values(filters).some(Boolean), [filters]);

  const getColumnSort = useCallback(
    (type: StatisticDefinition) => {
      if (sorting?.name !== type.name) return null;
      return order?.toLowerCase() as SortDirection;
    },
    [sorting, order],
  );

  const dataColumns = useMemo(
    () =>
      types.map<ColDef>((type) => ({
        field: type.name,
        flex: 1,
        minWidth: 60,
        headerName: type.displayName,
        headerClass: `ag-header--${type.name}`,
        sort: getColumnSort(type),
        context: { useShortLabel: false },
      })),
    [getColumnSort, types],
  );

  const pinned = !isDesktop ? 'left' : undefined;
  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: 'rank',
        minWidth: 0,
        width: isMobile ? 20 : 22,
        headerName: 'rank',
        headerClass: 'ag-header-cell--rank hideTitle',
        cellClass: 'pk-p--0 pk-justify-content--start pk-text--text-03 pk-font-size--xs pk-font-size-md--s',
        pinned,
        sortable: false,
      },
      {
        field: 'identifier',
        flex: 2,
        minWidth: isMobile ? 125 : 165,
        headerName: statType,
        headerClass: `ag-header-cell--identifier hideTitle`,
        cellClass: clsx(isMobile && 'pk-pl--0 pk-pr--xs2', 'pk-justify-content--start'),
        type: 'identifier',
        pinned,
      },
      ...dataColumns,
    ],
    [isMobile, dataColumns, statType, pinned],
  );

  const rowData = useMemo(
    () =>
      stats?.map((stat, index) => {
        const rowIndex = getRowIndex(index);
        return {
          ...Object.fromEntries(types.map((t) => [t.name, getStatValue(stat, t)])),
          identifier: stat[statType],
          team: statType === 'player' ? stat.team : undefined,
          rank: rowIndex,
        };
      }),
    [getRowIndex, statType, stats, types],
  );

  const showEmptyMessage = hasFiltersApplied && isFetched && !stats?.length;

  return (
    <div className='pk-d--flex pk-flex--column pk-w--100'>
      <Loader />
      {showEmptyMessage && <EmptyMessage />}
      {!!stats?.length && (
        <DataTableGrid
          key={`data-table-${pinned && 'pinned'}`}
          className={styles.table}
          defaultColDef={defaultColDef}
          columnDefs={columns}
          columnTypes={columnTypes}
          rowData={rowData}
          getRowId={getRowId}
          onSortChanged={handleSortingChange}
          sortable
          suppressMultiSort
          rowHeight={isMobile ? 60 : 80}
        />
      )}
      <div ref={scrollSpyRef} />
      {isFetchingNextPage && (
        <div data-testid='loader' className={styles.loader}>
          <div className={styles.bounce} />
          <div className={styles.bounce} />
          <div className={styles.bounce} />
        </div>
      )}
    </div>
  );
};

export default memo(Table);
